import type { Collection, IndexableType, Table } from 'dexie';

export type WhereDescriptor = {
	index: string;
	equals?: IndexableType;
	above?: IndexableType;
	below?: IndexableType;
	between?: [IndexableType, IndexableType];
	sort?: 'asc' | 'desc';
	limit?: number;
	filter?: (row: any) => boolean;
};

export function descriptor_to_collection<T>(
	table: Table<T, any>,
	descriptor: WhereDescriptor
): { collection: Collection<T, any>; post_filter: ((row: T) => boolean) | null } {
	let collection: Collection<T, any>;
	if (descriptor.equals !== undefined) {
		collection = table.where(descriptor.index).equals(descriptor.equals);
	} else if (descriptor.between !== undefined) {
		collection = table
			.where(descriptor.index)
			.between(descriptor.between[0], descriptor.between[1], true, true);
	} else if (descriptor.below !== undefined) {
		collection = table.where(descriptor.index).below(descriptor.below);
	} else if (descriptor.above !== undefined) {
		collection = table.where(descriptor.index).above(descriptor.above);
	} else {
		collection = table.orderBy(descriptor.index);
	}
	if (descriptor.sort === 'desc') collection = collection.reverse();
	if (descriptor.limit !== undefined) collection = collection.limit(descriptor.limit);
	const post_filter = descriptor.filter ?? null;
	return { collection, post_filter };
}

export function keyset_page<T>(
	table: Table<T, any>,
	options: {
		index: string;
		sort?: 'asc' | 'desc';
		page_size: number;
		before?: IndexableType;
		after?: IndexableType;
		filter?: (row: T) => boolean;
		skip?: number;
	}
): Promise<T[]> {
	const { index, sort = 'desc', page_size, before, after, filter, skip = 0 } = options;

	let collection: Collection<T, any>;
	if (before !== undefined) {
		collection = table.where(index).below(before);
	} else if (after !== undefined) {
		collection = table.where(index).above(after);
	} else {
		collection = table.orderBy(index);
	}
	if (sort === 'desc') collection = collection.reverse();
	if (filter) collection = collection.filter(filter);
	if (skip > 0) collection = collection.offset(skip);
	return collection.limit(page_size).toArray();
}

export function keyset_count<T>(
	table: Table<T, any>,
	options: { filter?: (row: T) => boolean }
): Promise<number> {
	const { filter } = options;
	let collection: Collection<T, any> = table.toCollection();
	if (filter) collection = collection.filter(filter);
	return collection.count();
}
